import {NgModule, Component, OnInit, inject, Injectable, PLATFORM_ID, Inject} from '@angular/core';
import {RouterModule, Routes, Router, UrlSegment, Route} from '@angular/router';
import {isPlatformBrowser, APP_BASE_HREF} from "@angular/common";
import {AdminAuthGuard} from './service/admin-auth-guard.service';
import {Shop_routes} from "./coreModule/shop/shop_routes";
import {Admin_routes} from "./coreModule/admin/admin_routes";
import {get_school_id} from "./service/school.service";

let school_id = get_school_id();

// Supported language codes
export const SUPPORTED_LANGS = ['tc', 'sc', 'en'];
const DEFAULT_LANG = 'tc';

/**
 * CanMatch guard that only allows the :lang route to match if the lang segment is valid.
 * This prevents /events from matching the :lang route (where events would be treated as lang).
 */
@Injectable({providedIn: 'root'})
export class LangCanMatchGuard {
	canMatch(route: Route, segments: UrlSegment[]): boolean {
		if (segments.length === 0) {
			return false;
		}
		const firstSegment = segments[0].path;
		const isValidLang = SUPPORTED_LANGS.includes(firstSegment);
		return isValidLang;
	}
}

/**
 * Component that handles redirecting URLs without a valid language prefix.
 * It reads the actual browser URL and redirects with the language prefix prepended.
 * Uses window.location.replace for a clean redirect without Angular router interference.
 * 
 * Examples:
 *   - /           -> /tc
 *   - /events     -> /tc/events
 *   - /events/123 -> /tc/events/123
 * 
 * ============================================================================
 * SSR MIGRATION NOTICE (Angular Universal)
 * ============================================================================
 * This component uses `window.location` which is NOT available on the server.
 * When converting to SSR, you need to update this component AND add server-side
 * redirect handling for proper SEO (HTTP 301/302 redirects).
 * 
 * OPTION 1: Update this component to be SSR-compatible
 * ----------------------------------------------------------------------------
 * ```typescript
 * import {PLATFORM_ID, Inject} from '@angular/core';
 * import {isPlatformBrowser, DOCUMENT} from '@angular/common';
 * 
 * @Component({template: ''})
 * export class LangRedirectComponent implements OnInit {
 *   private router = inject(Router);
 *   
 *   constructor(
 *     @Inject(PLATFORM_ID) private platformId: Object,
 *     @Inject(DOCUMENT) private document: Document,
 *   ) {}
 * 
 *   ngOnInit() {
 *     if (isPlatformBrowser(this.platformId)) {
 *       // Client-side: use window.location.replace (current implementation)
 *       // ... existing code ...
 *     } else {
 *       // Server-side: use Angular's router
 *       const url = this.router.url;
 *       const pathSegments = url.split('/').filter(s => s && !s.startsWith('?'));
 *       if (pathSegments.length > 0 && SUPPORTED_LANGS.includes(pathSegments[0])) {
 *         return;
 *       }
 *       const newPath = url === '/' ? `/${DEFAULT_LANG}` : `/${DEFAULT_LANG}${url}`;
 *       this.router.navigateByUrl(newPath, {replaceUrl: true});
 *     }
 *   }
 * }
 * ```
 * 
 * OPTION 2: Add Express.js middleware for proper HTTP redirects (RECOMMENDED for SEO)
 * ----------------------------------------------------------------------------
 * In your server.ts file, add this middleware BEFORE Angular Universal handler:
 * 
 * ```typescript
 * // server.ts
 * const SUPPORTED_LANGS = ['tc', 'sc', 'en'];
 * const DEFAULT_LANG = 'tc';
 * 
 * app.use((req, res, next) => {
 *   const pathSegments = req.path.split('/').filter(s => s);
 *   
 *   // If path doesn't start with a valid language code, redirect with 301
 *   if (pathSegments.length === 0 || !SUPPORTED_LANGS.includes(pathSegments[0])) {
 *     const newPath = req.path === '/' ? `/${DEFAULT_LANG}` : `/${DEFAULT_LANG}${req.path}`;
 *     const queryString = req.url.includes('?') ? req.url.substring(req.url.indexOf('?')) : '';
 *     return res.redirect(301, newPath + queryString);
 *   }
 *   next();
 * });
 * ```
 * 
 * OPTION 3: Nginx configuration (BEST for production SEO)
 * ----------------------------------------------------------------------------
 * Add this to your Nginx server block for fastest redirects:
 * 
 * ```nginx
 * location / {
 *   # Redirect if path doesn't start with a valid language code
 *   if ($uri !~ ^/(tc|sc|en)(/|$)) {
 *     rewrite ^/$ /tc permanent;
 *     rewrite ^/(.*)$ /tc/$1 permanent;
 *   }
 *   # ... rest of your Angular Universal config
 * }
 * ```
 * ============================================================================
 */
@Component({
	template: '',
	standalone: false
})
export class LangRedirectComponent implements OnInit {
	private isBrowser: boolean;
	
	constructor(
		@Inject(PLATFORM_ID) platformId: Object,
		private router: Router
	) {
		this.isBrowser = isPlatformBrowser(platformId);
	}
	
	ngOnInit() {
		if (this.isBrowser) {
			// Client-side: use window.location.replace for a clean redirect
			const actualPath = window.location.pathname;
			const queryString = window.location.search;
			const hash = window.location.hash;
			
			// Check if already has valid lang prefix (safety check)
			const pathSegments = actualPath.split('/').filter(s => s);
			if (pathSegments.length > 0 && SUPPORTED_LANGS.includes(pathSegments[0])) {
				return;
			}
			
			// Build new URL with language prefix
			const newPath = actualPath === '/' ? `/${DEFAULT_LANG}` : `/${DEFAULT_LANG}${actualPath}`;
			const fullUrl = window.location.origin + newPath + queryString + hash;
			
			// Use location.replace for a clean redirect that replaces the current history entry
			window.location.replace(fullUrl);
		} else {
			// Server-side: use Angular's router (though server.ts middleware should handle this)
			const url = this.router.url;
			const pathSegments = url.split('/').filter(s => s && !s.startsWith('?'));
			if (pathSegments.length > 0 && SUPPORTED_LANGS.includes(pathSegments[0])) {
				return;
			}
			const newPath = url === '/' ? `/${DEFAULT_LANG}` : `/${DEFAULT_LANG}${url}`;
			this.router.navigateByUrl(newPath, {replaceUrl: true});
		}
	}
}

/**
 * if school_id is 1568, this means the user is accessing our company's online shop
 * if school_id is not 1568, this means the user is accessing a school's online shop
 */
// Comment out for the new online shop product don't need to use school_id, we use loginDomain instead and each new online shop should be deploy as a new app on a specific domain
// const routes: Routes = (school_id !== 1568 ? [
// 	{path: ':school/:lang/admin', children: Admin_routes},
// 	{path: ':school/:lang', children: Shop_routes},
// 	{path: ':school', redirectTo: ':school/tc', pathMatch: 'full',},
// ] : [
// 	{path: ':lang/admin', children: Admin_routes},
// 	{path: ':lang', children: Shop_routes},
// 	{path: '', redirectTo: 'tc', pathMatch: 'full',},
// ]);

const routes: Routes = [
	// Comment out admin routes for the new online shop product, as the admin panel is in ROWeb
	// {path: ':lang/admin', children: Admin_routes},
	// Route with :lang parameter, but guarded by canMatch to only allow valid lang codes
	{path: ':lang', children: Shop_routes, canMatch: [LangCanMatchGuard]},
	// Catch-all: any path without a valid lang prefix gets redirected with lang prefix prepended
	// This includes root path (/) -> /tc, and /events -> /tc/events, etc.
	{path: '', redirectTo: 'tc', pathMatch: 'full'}, // Redirect root to default language
	{path: '**', component: LangRedirectComponent},
];

@NgModule({
	declarations: [LangRedirectComponent],
	imports: [RouterModule.forRoot(routes, {
    initialNavigation: 'enabledBlocking'
})],
	exports: [RouterModule],
	providers: [
		AdminAuthGuard,
		{provide: APP_BASE_HREF, useValue: `/`}
	]
})
export class AppRoutingModule {
}
